import { defineStore } from 'pinia'
import { Capacitor } from '@capacitor/core'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'
import { useAppBadge } from '@/composables/useAppBadge'

let storeAuth = null
let realtimeSubscription = null
let appBadge = null

// Debounce timer for server badge sync
let badgeSyncTimeout = null

/**
 * Activity types for filtering
 */
export const ACTIVITY_TYPES = {
  REVIEW_CREATE: 'review_create',
  REVIEW_UPDATE: 'review_update',
  LIST_CREATE: 'list_create',
  LIST_UPDATE: 'list_update',
  LIST_FOLLOW: 'list_follow',
  LIST_ITEM_ADD: 'list_item_add',
  LIST_ITEM_REMOVE: 'list_item_remove',
  LIST_COLLABORATIVE: 'list_collaborative',
  USER_FOLLOW: 'user_follow',
  USER_UNFOLLOW: 'user_unfollow',
  PROFILE_UPDATE: 'profile_update',
  SYSTEM_MESSAGE: 'system_message',
  // Message-related types (received)
  MESSAGE_RECEIVED: 'message_received',
  REPLY_RECEIVED: 'reply_received',
  REACTION_RECEIVED: 'reaction_received',
  // Message-related types (sent)
  MESSAGE_SENT: 'message_sent',
  REACTION_SENT: 'reaction_sent',
  // Comment-related types
  COMMENT_RECEIVED: 'comment_received',
  // One item per data import (docs/38): `data` carries the counts
  IMPORT_SUMMARY: 'import_summary',
  // Title state change (watchlist/watching/seen transitions)
  TITLE_STATE_CHANGE: 'title_state_change'
}

/**
 * Actionable item types - items that require user response/action
 * These items track individual read/unread state
 */
export const ACTIONABLE_TYPES = {
  LIST_INVITE_RECEIVED: 'list_invite_received',
  TEST_NOTIFICATION: 'test_notification'
  // Future: message_received, comment_received, etc.
}

/**
 * Check if an item is actionable based on activity type and data
 * @param {object} item - Feed item
 * @returns {boolean}
 */
export function isActionableItem(item) {
  // System messages with specific types
  if (item.activity_type === ACTIVITY_TYPES.SYSTEM_MESSAGE) {
    const type = item.data?.type
    return Object.values(ACTIONABLE_TYPES).includes(type)
  }
  return false
}

/**
 * Special filter key for unread items (not activity-type based)
 */
export const SPECIAL_FILTERS = {
  UNREAD: 'UNREAD'
}

/**
 * Filter presets for the feed view
 */
export const FEED_FILTERS = {
  ALL: [],
  UNREAD: SPECIAL_FILTERS.UNREAD, // Special filter - not activity type based
  REVIEWS: [ACTIVITY_TYPES.REVIEW_CREATE, ACTIVITY_TYPES.REVIEW_UPDATE],
  LISTS: [
    ACTIVITY_TYPES.LIST_CREATE,
    ACTIVITY_TYPES.LIST_UPDATE,
    ACTIVITY_TYPES.LIST_ITEM_ADD,
    ACTIVITY_TYPES.LIST_ITEM_REMOVE,
    ACTIVITY_TYPES.LIST_COLLABORATIVE
  ],
  WATCHING: [ACTIVITY_TYPES.TITLE_STATE_CHANGE],
  // Combined: user messages + system notifications
  MESSAGES: [
    ACTIVITY_TYPES.MESSAGE_RECEIVED,
    ACTIVITY_TYPES.REPLY_RECEIVED,
    ACTIVITY_TYPES.REACTION_RECEIVED,
    ACTIVITY_TYPES.COMMENT_RECEIVED,
    ACTIVITY_TYPES.MESSAGE_SENT,
    ACTIVITY_TYPES.REACTION_SENT,
    ACTIVITY_TYPES.SYSTEM_MESSAGE
  ],
  FOLLOWING: [
    ACTIVITY_TYPES.USER_FOLLOW,
    ACTIVITY_TYPES.USER_UNFOLLOW,
    ACTIVITY_TYPES.LIST_FOLLOW,
    ACTIVITY_TYPES.PROFILE_UPDATE
  ]
}

export const useStoreFeed = defineStore('storeFeed', {
  state: () => ({
    // Feed items
    items: [],

    // Loading states
    loading: false,
    loadingMore: false,
    loaded: false,

    // Error state
    error: null,

    // Pagination
    hasMore: false,
    offset: 0,
    limit: 20,

    // Read state
    unreadCount: 0,
    lastSeenId: null,
    lastSeenAt: null,

    // Actionable unread count (fetched from server)
    actionableUnreadCount: 0,

    // Current filter
    activeFilter: 'ALL',
    activeTypes: []
  }),

  getters: {
    /**
     * Get all feed items
     */
    feedItems: (state) => state.items,

    /**
     * Get unread items (items newer than lastSeenAt)
     */
    unreadItems: (state) => {
      if (!state.lastSeenAt) return state.items
      return state.items.filter(
        (item) => new Date(item.created_at) > new Date(state.lastSeenAt)
      )
    },

    /**
     * Get actionable items (items that require user action) from loaded items
     */
    actionableItems: (state) => {
      return state.items.filter((item) => isActionableItem(item))
    },

    /**
     * Get unread actionable items from loaded items
     */
    unreadActionableItems: (state) => {
      return state.items.filter(
        (item) => isActionableItem(item) && !item.is_read
      )
    },

    /**
     * Check if feed is loading
     */
    isLoading: (state) => state.loading,

    /**
     * Check if there's more to load
     */
    canLoadMore: (state) => state.hasMore && !state.loadingMore,

    /**
     * Get feed items grouped by date
     */
    itemsByDate: (state) => {
      const groups = {}
      state.items.forEach((item) => {
        const date = new Date(item.created_at).toLocaleDateString()
        if (!groups[date]) groups[date] = []
        groups[date].push(item)
      })
      return groups
    }
  },

  actions: {
    /**
     * Initialize the feed store
     */
    init() {
      storeAuth = useStoreAuth()
      appBadge = useAppBadge()
      if (!storeAuth.user) {
        console.log('[storeFeed] Skipping init - user not authenticated')
        return
      }
      this.fetch()
      this.fetchActionableUnreadCount()
      this.subscribeToRealtime()
    },

    /**
     * Update iOS app badge locally (immediate) and sync with server (debounced)
     * Local update provides instant feedback, server sync ensures push notifications have correct badge
     */
    syncBadge() {
      // Only sync on native iOS
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
        return
      }

      // The app icon badge reflects unread notifications (single definition);
      // storeNotifications owns the local badge value
      import('@/stores/storeNotifications').then(
        ({ useStoreNotifications }) => {
          useStoreNotifications().syncAppBadge()
        }
      )

      // Debounced server sync for push notification badge consistency
      if (badgeSyncTimeout) {
        clearTimeout(badgeSyncTimeout)
      }

      badgeSyncTimeout = setTimeout(async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession()
          if (!sessionData?.session?.access_token) {
            console.log('[storeFeed] No session for badge sync')
            return
          }

          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          const response = await fetch(
            `${supabaseUrl}/functions/v1/push/sync-badge`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${sessionData.session.access_token}`,
                'Content-Type': 'application/json'
              }
            }
          )

          if (response.ok) {
            const result = await response.json()
            console.log('[storeFeed] Server badge synced:', result)
          } else {
            console.error(
              '[storeFeed] Server badge sync failed:',
              response.status
            )
          }
        } catch (err) {
          console.error('[storeFeed] Error syncing badge with server:', err)
        }
      }, 500) // 500ms debounce for server sync
    },

    /**
     * Fetch feed items from the database view
     * @param {object} options - Fetch options
     * @param {boolean} options.refresh - If true, reset pagination and fetch fresh
     * @param {string[]|string} options.types - Activity types to filter by, or special filter key
     */
    async fetch(options = {}) {
      const { refresh = true, types = this.activeTypes } = options

      if (refresh) {
        this.loading = true
        this.offset = 0
        this.items = []
      } else {
        this.loadingMore = true
      }

      this.error = null

      try {
        // Build query using the view directly
        let query = supabase
          .from('feed_items_view')
          .select('*')
          .order('created_at', { ascending: false })
          .range(this.offset, this.offset + this.limit)

        // Handle special UNREAD filter - only actionable items (system messages) that are unread
        // These are the items that count towards the app badge
        if (types === SPECIAL_FILTERS.UNREAD) {
          query = query
            .eq('activity_type', 'system_message')
            .eq('is_read', false)
        } else if (Array.isArray(types) && types.length > 0) {
          // Filter by activity types if specified
          query = query.in('activity_type', types)
        }

        const { data, error } = await query

        if (error) throw error

        const items = data || []

        // Update state
        if (refresh) {
          this.items = items
        } else {
          // Append for pagination
          this.items = [...this.items, ...items]
        }

        // Check if there are more items (if we got a full page, there might be more)
        this.hasMore = items.length === this.limit + 1
        if (this.hasMore) {
          // Remove the extra item we fetched to check for more
          this.items.pop()
        }

        // Only increment offset by items we actually kept (not the extra check item)
        this.offset += this.hasMore ? items.length - 1 : items.length
        this.loaded = true

        // Fetch read state separately
        await this.fetchReadState()
      } catch (err) {
        console.error('[storeFeed] Error fetching feed:', err)
        this.error = err.message || 'Failed to load feed'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    /**
     * Fetch the user's read state
     */
    async fetchReadState() {
      try {
        const { data, error } = await supabase
          .from('feed_read_state')
          .select('last_seen_id, last_seen_at')
          .maybeSingle()

        if (error) throw error

        this.lastSeenId = data?.last_seen_id || null
        this.lastSeenAt = data?.last_seen_at || null

        // Calculate unread count
        if (this.lastSeenAt) {
          const unreadItems = this.items.filter(
            (item) => new Date(item.created_at) > new Date(this.lastSeenAt)
          )
          this.unreadCount = unreadItems.length
        } else {
          this.unreadCount = this.items.length
        }
      } catch (err) {
        console.error('[storeFeed] Error fetching read state:', err)
      }
    },

    /**
     * Fetch the count of unread actionable items from the server
     * This runs independently of the loaded feed items
     * Also syncs the badge with server to fix stale badge counts
     */
    async fetchActionableUnreadCount() {
      try {
        // Query feed_items for all unread system messages
        // This includes list invites, test notifications, and other system messages
        const { count, error } = await supabase
          .from('feed_items')
          .select('*', { count: 'exact', head: true })
          .eq('activity_type', 'system_message')
          .eq('is_read', false)
          .is('deleted_at', null)

        if (error) throw error

        const newCount = count || 0
        const hadDifferentCount = this.actionableUnreadCount !== newCount
        this.actionableUnreadCount = newCount

        console.log(
          '[storeFeed] Actionable unread count:',
          this.actionableUnreadCount
        )

        // Always sync badge on fetch to fix stale badge counts
        // This updates both local badge and server (for push notifications)
        this.syncBadge()

        // Log if we corrected a mismatch (helps debug stale badge issues)
        if (hadDifferentCount) {
          console.log('[storeFeed] Badge count corrected from mismatch')
        }
      } catch (err) {
        console.error(
          '[storeFeed] Error fetching actionable unread count:',
          err
        )
      }
    },

    /**
     * Load more items (pagination)
     */
    async loadMore() {
      if (!this.hasMore || this.loadingMore) return
      await this.fetch({ refresh: false })
    },

    /**
     * Refresh the feed (pull-to-refresh)
     */
    async refresh() {
      await this.fetch({ refresh: true })
    },

    /**
     * Refresh feed in the background without showing loading state.
     * Replaces items silently so new feed entries appear without a flash.
     */
    async refreshInBackground() {
      try {
        let query = supabase
          .from('feed_items_view')
          .select('*')
          .order('created_at', { ascending: false })
          .range(0, this.limit)

        if (this.activeTypes === SPECIAL_FILTERS.UNREAD) {
          query = query
            .eq('activity_type', 'system_message')
            .eq('is_read', false)
        } else if (
          Array.isArray(this.activeTypes) &&
          this.activeTypes.length > 0
        ) {
          query = query.in('activity_type', this.activeTypes)
        }

        const { data, error } = await query

        if (error) throw error

        this.items = data || []
        this.hasMore = this.items.length === this.limit + 1
        if (this.hasMore) this.items.pop()
        this.offset = this.items.length
        this.loaded = true
      } catch (err) {
        console.error('[storeFeed] Background refresh error:', err)
      }
    },

    /**
     * Refresh after app resumes from background
     * Reconnects realtime subscriptions and fetches fresh data
     */
    async refreshAfterResume() {
      console.log('[storeFeed] Refreshing after app resume')

      // Unsubscribe from old channel (it may be stale/disconnected)
      this.unsubscribeFromRealtime()

      // Fetch fresh data and actionable count
      await Promise.all([
        this.fetch({ refresh: true }),
        this.fetchActionableUnreadCount()
      ])

      // Re-subscribe to realtime changes
      this.subscribeToRealtime()
    },

    /**
     * Set the active filter and refresh
     * @param {string} filterKey - One of FEED_FILTERS keys
     */
    async setFilter(filterKey) {
      if (!FEED_FILTERS[filterKey]) {
        console.warn(`[storeFeed] Unknown filter: ${filterKey}`)
        return
      }

      this.activeFilter = filterKey
      this.activeTypes = FEED_FILTERS[filterKey]
      await this.fetch({ refresh: true, types: this.activeTypes })
    },

    /**
     * Mark feed as read up to the most recent item
     */
    async markAsRead() {
      if (this.items.length === 0) return

      const mostRecentItem = this.items[0]
      if (!mostRecentItem?.id) return

      // Optimistic update
      const previousUnreadCount = this.unreadCount
      this.unreadCount = 0
      this.lastSeenId = mostRecentItem.id
      this.lastSeenAt = mostRecentItem.created_at

      try {
        // Upsert read state directly
        const { error } = await supabase.from('feed_read_state').upsert({
          user_id: storeAuth.user.id,
          last_seen_id: mostRecentItem.id,
          last_seen_at: mostRecentItem.created_at,
          updated_at: new Date().toISOString()
        })

        if (error) throw error
      } catch (err) {
        console.error('[storeFeed] Error marking as read:', err)
        // Revert optimistic update
        this.unreadCount = previousUnreadCount
      }
    },

    /**
     * Delete a feed item (soft delete)
     * @param {string} itemId - Feed item ID to delete
     */
    async deleteItem(itemId) {
      console.log('[storeFeed] deleteItem called with:', itemId)
      console.log('[storeFeed] Current items:', this.items.length)

      // Optimistic update - remove from list
      const itemIndex = this.items.findIndex((item) => item.id === itemId)
      console.log('[storeFeed] Found item at index:', itemIndex)
      const removedItem = itemIndex >= 0 ? this.items[itemIndex] : null

      // Check if this was an unread system message for badge sync
      const wasUnreadSystemMessage =
        removedItem &&
        removedItem.activity_type === 'system_message' &&
        !removedItem.is_read

      if (itemIndex >= 0) {
        this.items.splice(itemIndex, 1)
      }

      // Decrement actionable count if it was an unread system message
      if (wasUnreadSystemMessage) {
        this.actionableUnreadCount = Math.max(0, this.actionableUnreadCount - 1)
        console.log(
          '[storeFeed] Decremented count after delete to:',
          this.actionableUnreadCount
        )
      }

      try {
        // Soft delete by setting deleted_at
        const { error } = await supabase
          .from('feed_items')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', itemId)

        if (error) throw error

        // Sync iOS badge after successful delete
        if (wasUnreadSystemMessage) {
          this.syncBadge()
        }
      } catch (err) {
        console.error('[storeFeed] Error deleting item:', err)
        // Revert optimistic update
        if (removedItem && itemIndex >= 0) {
          this.items.splice(itemIndex, 0, removedItem)
        }
        // Revert actionable count
        if (wasUnreadSystemMessage) {
          this.actionableUnreadCount++
        }
      }
    },

    /**
     * Mark a specific feed item as read
     * @param {string} itemId - Feed item ID to mark as read
     */
    async markItemAsRead(itemId) {
      const item = this.items.find((i) => i.id === itemId)
      const wasUnread = item && !item.is_read
      const actionable = item ? isActionableItem(item) : false

      console.log('[storeFeed] markItemAsRead:', {
        itemId,
        found: !!item,
        wasUnread,
        actionable,
        activityType: item?.activity_type,
        dataType: item?.data?.type,
        currentCount: this.actionableUnreadCount
      })

      // Optimistic update
      if (item) item.is_read = true
      if (wasUnread && actionable) {
        this.actionableUnreadCount = Math.max(0, this.actionableUnreadCount - 1)
        console.log(
          '[storeFeed] Decremented count to:',
          this.actionableUnreadCount
        )
      }

      try {
        const { error } = await supabase
          .from('feed_items')
          .update({ is_read: true })
          .eq('id', itemId)

        if (error) throw error
        console.log('[storeFeed] Marked item as read:', itemId)

        // Sync iOS badge after successful update
        if (wasUnread && actionable) {
          this.syncBadge()
        }
      } catch (err) {
        console.error('[storeFeed] Error marking item as read:', err)
        // Revert optimistic update
        if (item) item.is_read = false
        if (wasUnread && actionable) {
          this.actionableUnreadCount++
        }
      }
    },

    /**
     * Mark a specific feed item as unread
     * @param {string} itemId - Feed item ID to mark as unread
     */
    async markItemAsUnread(itemId) {
      const item = this.items.find((i) => i.id === itemId)
      const wasRead = item && item.is_read
      const actionable = item ? isActionableItem(item) : false

      console.log('[storeFeed] markItemAsUnread:', {
        itemId,
        found: !!item,
        wasRead,
        actionable,
        activityType: item?.activity_type,
        dataType: item?.data?.type,
        currentCount: this.actionableUnreadCount
      })

      // Optimistic update
      if (item) item.is_read = false
      if (wasRead && actionable) {
        this.actionableUnreadCount++
        console.log(
          '[storeFeed] Incremented count to:',
          this.actionableUnreadCount
        )
      }

      try {
        const { error } = await supabase
          .from('feed_items')
          .update({ is_read: false })
          .eq('id', itemId)

        if (error) throw error
        console.log('[storeFeed] Marked item as unread:', itemId)

        // Sync iOS badge after successful update
        if (wasRead && actionable) {
          this.syncBadge()
        }
      } catch (err) {
        console.error('[storeFeed] Error marking item as unread:', err)
        // Revert optimistic update
        if (item) item.is_read = true
        if (wasRead && actionable) {
          this.actionableUnreadCount = Math.max(
            0,
            this.actionableUnreadCount - 1
          )
        }
      }
    },

    /**
     * Toggle read state for a feed item
     * @param {string} itemId - Feed item ID to toggle
     */
    async toggleItemReadState(itemId) {
      const item = this.items.find((i) => i.id === itemId)
      if (!item) return

      if (item.is_read) {
        await this.markItemAsUnread(itemId)
      } else {
        await this.markItemAsRead(itemId)
      }
    },

    /**
     * Mark all actionable items as read
     */
    async markAllActionableAsRead() {
      const previousCount = this.actionableUnreadCount

      // Optimistic update - mark loaded items and reset count
      const unreadActionable = this.items.filter(
        (item) => isActionableItem(item) && !item.is_read
      )
      unreadActionable.forEach((item) => {
        item.is_read = true
      })
      this.actionableUnreadCount = 0

      try {
        // Mark ALL unread system message items on server (not just loaded ones)
        const { error } = await supabase
          .from('feed_items')
          .update({ is_read: true })
          .eq('activity_type', 'system_message')
          .eq('is_read', false)
          .is('deleted_at', null)

        if (error) throw error
        console.log('[storeFeed] Marked all actionable items as read')

        // Sync iOS badge after successful update
        if (previousCount > 0) {
          this.syncBadge()
        }
      } catch (err) {
        console.error('[storeFeed] Error marking all actionable as read:', err)
        // Revert optimistic update
        unreadActionable.forEach((item) => {
          item.is_read = false
        })
        this.actionableUnreadCount = previousCount
      }
    },

    /**
     * Subscribe to real-time feed updates
     */
    subscribeToRealtime() {
      if (realtimeSubscription) return

      const userId = storeAuth?.user?.id
      if (!userId) return

      realtimeSubscription = supabase
        .channel('feed_realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feed_items',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('[storeFeed] New feed item received:', payload)
            const newItem = payload.new

            // Increment general unread count
            this.unreadCount++

            // If this is an actionable item (system message), increment that count too
            if (
              newItem.activity_type === 'system_message' &&
              !newItem.is_read
            ) {
              this.actionableUnreadCount++
              console.log(
                '[storeFeed] Incremented actionable count to:',
                this.actionableUnreadCount
              )
            }

            // Refresh from the view to get the full joined data (actor,
            // list, review, etc.) rather than prepending the raw table row
            // which would be missing joined fields and get filtered out.
            this.refreshInBackground()
          }
        )
        .subscribe()
    },

    /**
     * Unsubscribe from real-time updates
     */
    unsubscribeFromRealtime() {
      if (realtimeSubscription) {
        supabase.removeChannel(realtimeSubscription)
        realtimeSubscription = null
      }
    },

    /**
     * Clear the feed state
     */
    clear() {
      const hadUnread = this.actionableUnreadCount > 0
      this.items = []
      this.loaded = false
      this.loading = false
      this.error = null
      this.hasMore = false
      this.offset = 0
      this.unreadCount = 0
      this.lastSeenId = null
      this.lastSeenAt = null
      this.actionableUnreadCount = 0
      this.unsubscribeFromRealtime()

      // Sync badge to 0 on logout
      if (hadUnread) {
        this.syncBadge()
      }
    },

    /**
     * Update items (merge with existing)
     * @deprecated Use fetch() instead
     */
    updateItems(fetchedItems) {
      const itemsMap = new Map(this.items.map((item) => [item.id, item]))

      fetchedItems.forEach((fetchedItem) => {
        if (itemsMap.has(fetchedItem.id)) {
          Object.assign(itemsMap.get(fetchedItem.id), fetchedItem)
        } else {
          itemsMap.set(fetchedItem.id, fetchedItem)
        }
      })

      this.items = Array.from(itemsMap.values())
    }
  }
})
